import { createFileRoute, useParams } from "@tanstack/react-router";
import { signInWithCustomToken } from "firebase/auth";
import { Loader2, Mic, MicOff, CheckCircle2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { Button } from "@scribe/ui/button";
import { auth, storage } from "@/lib/firebase/client";
import { exchangeMobileRecordingToken, startTranscription } from "@/lib/apiSdk";
import { isUsageLimitError, usageLimitMessage } from "@/lib/usage-errors";
import { buildRecordingPath } from "@/lib/media/storagePaths";
import { uploadChunkToFirebase } from "@/lib/firebase/firebaseStorageHandler";
import { useOrgStore } from "@/stores/org-store";

export const Route = createFileRoute("/mobile/record/$token")({
  component: MobileRecordPage,
});

const MIME_PREFERENCE = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
  "audio/aac",
];

function getSupportedMimeType(): string {
  return MIME_PREFERENCE.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
}

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0)
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

type Phase =
  | "exchanging"
  | "ready"
  | "recording"
  | "uploading"
  | "done"
  | "error";

interface SessionCtx {
  clientId: string;
  eventId: string;
  orgId: string;
  userId: string;
}

function MobileRecordPage() {
  const { token } = useParams({ from: "/mobile/record/$token" });

  const [phase, setPhase] = useState<Phase>("exchanging");
  const [error, setError] = useState<string | null>(null);
  const [sessionCtx, setSessionCtx] = useState<SessionCtx | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const hasExchanged = useRef(false);

  // Phase A: exchange token on mount
  useEffect(() => {
    if (hasExchanged.current) return;
    hasExchanged.current = true;

    (async () => {
      try {
        const result = await exchangeMobileRecordingToken(token);
        const cred = await signInWithCustomToken(auth, result.custom_token);
        const uid = cred.user.uid;

        // Wire up org context so withAuthHeaders() attaches X-Org-Id for startTranscription
        useOrgStore
          .getState()
          .setOrgData({ [result.org_id]: "member" }, result.org_id);

        setSessionCtx({
          clientId: result.client_id,
          eventId: result.event_id,
          orgId: result.org_id,
          userId: uid,
        });
        setPhase("ready");
      } catch {
        setError("This recording link is invalid or has already been used.");
        setPhase("error");
      }
    })();
  }, [token]);

  async function handleStart() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      startTimeRef.current = Date.now();
      setElapsed(0);
      setPhase("recording");

      timerRef.current = setInterval(() => {
        setElapsed((s) => s + 1);
      }, 1000);
    } catch {
      setError(
        "Could not access microphone. Please allow microphone access and try again."
      );
    }
  }

  async function handleStop() {
    if (!sessionCtx) return;

    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setPhase("uploading");

    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });

    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    const chunks = chunksRef.current;
    const mimeType = getSupportedMimeType() || "audio/webm";
    const ext =
      mimeType.startsWith("audio/mp4") || mimeType.startsWith("audio/aac")
        ? "mp4"
        : mimeType.startsWith("audio/ogg")
          ? "ogg"
          : "webm";

    const blob = new Blob(chunks, { type: mimeType });
    const recordingId = uuidv4();
    const lengthSeconds = startTimeRef.current
      ? Math.max(1, Math.round((Date.now() - startTimeRef.current) / 1000))
      : undefined;

    try {
      const path = buildRecordingPath({
        userId: sessionCtx.userId,
        clientId: sessionCtx.clientId,
        eventId: sessionCtx.eventId,
        recordingId,
        ext,
      });

      const gsUri = await uploadChunkToFirebase(storage, path, blob, (p) =>
        setUploadProgress(Math.round(p))
      );

      await startTranscription({
        client_id: sessionCtx.clientId,
        event_id: sessionCtx.eventId,
        audio_url: gsUri,
        length_seconds: lengthSeconds,
      });

      setPhase("done");
    } catch (err) {
      // The audio reached storage; only the transcription was refused. Saying
      // "check your connection" here would send the clinician chasing the
      // wrong problem.
      if (isUsageLimitError(err)) {
        setError(
          `Your recording was uploaded, but transcription was not started. ${usageLimitMessage(err)}`
        );
      } else {
        setError("Upload failed. Please check your connection and try again.");
      }
      setPhase("error");
    }
  }

  return (
    <div className="bg-background flex min-h-screen flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-8">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            Session Recording
          </h1>
          <p className="text-muted-foreground text-sm">
            {phase === "exchanging" && "Connecting…"}
            {phase === "ready" && "Ready to record"}
            {phase === "recording" && "Recording in progress"}
            {phase === "uploading" && "Uploading…"}
            {phase === "done" && "Recording complete"}
            {phase === "error" && "Something went wrong"}
          </p>
        </div>

        {phase === "exchanging" && (
          <div className="flex justify-center">
            <Loader2 className="text-muted-foreground h-10 w-10 animate-spin" />
          </div>
        )}

        {phase === "ready" && (
          <Button
            size="lg"
            className="h-16 w-full gap-3 text-lg"
            onClick={handleStart}
          >
            <Mic className="h-6 w-6" />
            Start Recording
          </Button>
        )}

        {phase === "recording" && (
          <div className="space-y-6">
            <div className="text-center">
              <span className="font-mono text-5xl font-light tabular-nums">
                {formatElapsed(elapsed)}
              </span>
            </div>
            <div className="flex justify-center">
              <div className="bg-destructive h-3 w-3 animate-pulse rounded-full" />
            </div>
            <Button
              size="lg"
              variant="destructive"
              className="h-16 w-full gap-3 text-lg"
              onClick={handleStop}
            >
              <MicOff className="h-6 w-6" />
              Stop & Upload
            </Button>
          </div>
        )}

        {phase === "uploading" && (
          <div className="space-y-4 text-center">
            <Loader2 className="text-muted-foreground mx-auto h-10 w-10 animate-spin" />
            <p className="text-muted-foreground text-sm">
              {uploadProgress > 0
                ? `Uploading… ${uploadProgress}%`
                : "Preparing upload…"}
            </p>
          </div>
        )}

        {phase === "done" && (
          <div className="space-y-4 text-center">
            <CheckCircle2 className="text-success mx-auto h-16 w-16" />
            <div>
              <p className="font-medium">Recording submitted</p>
              <p className="text-muted-foreground mt-1 text-sm">
                You can close this tab.
              </p>
            </div>
          </div>
        )}

        {phase === "error" && error && (
          <div className="border-destructive/30 bg-destructive/10 text-destructive rounded-lg border p-4 text-center text-sm">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
